/**
 * Design validator for the CHE 334 Virtual Pumping System Lab.
 *
 * Consumes placed components, scenario constraints, fluid data,
 * and (optionally) simulation engine output to produce a structured
 * list of hard failures and soft penalties.
 *
 * Each result item: { severity: 'hard' | 'soft', code: string, message: string }
 */

import { getFluid, isMaterialCompatible } from "@/data/fluids.js";
import { M_TO_FT } from "@/utils/units.js";

// ─── Thresholds ──────────────────────────────────────────────────────

const MIN_EFFICIENCY = 0.3; // below 30% → soft penalty
const MAX_VELOCITY_FPS = 10.0; // above 10 ft/s → soft penalty (≈ 3 m/s)
const MAX_MINOR_LOSS_RATIO = 0.6; // minor > 60% of total friction → excessive fittings
const OVERSIZE_VELOCITY_FPS = 1.6; // velocity below 1.6 ft/s → pipe probably over-designed (≈ 0.5 m/s)

// ─── Helper: extract material from a placed component ────────────────

function componentMaterial(comp) {
  // Pipes have comp.material; fittings may have comp.materials (array) or comp.material
  return comp.material || comp.materials?.[0] || null;
}

// ─── Sub-validators ──────────────────────────────────────────────────

function checkRequiredComponents(components, results) {
  const pipes = components.filter((c) => c.category === "pipe");
  const pumps = components.filter((c) => c.category === "pump");

  if (pumps.length === 0) {
    results.push({
      severity: "hard",
      code: "NO_PUMP",
      message:
        "Design has no pump. A pumping system requires at least one pump."
    });
  }
  if (pipes.length === 0) {
    results.push({
      severity: "hard",
      code: "NO_PIPE",
      message:
        "Design has no pipe segments. A pumping system requires at least one pipe."
    });
  }
}

function checkMaterialCompatibility(components, scenario, fluid, results) {
  if (!fluid) return;
  components.forEach((comp) => {
    const mat = componentMaterial(comp);
    if (mat && !isMaterialCompatible(scenario.fluidKey, mat)) {
      results.push({
        severity: "hard",
        code: "MATERIAL_INCOMPATIBLE",
        message: `${comp.name} (${mat}) is not compatible with ${fluid.name}. Compatible materials: ${fluid.compatibleMaterials.join(", ")}.`
      });
    }
  });
}

function checkSimulationHard(simResult, scenario, results) {
  if (!simResult) return;

  if (!simResult.success) {
    results.push({
      severity: "hard",
      code: "NO_OPERATING_POINT",
      message:
        simResult.reason ||
        "The pump cannot overcome the system head. Choose a larger pump or reduce system losses."
    });
    return;
  }

  const targetQ = scenario.targetFlowRate_m3s;
  const actualQ = simResult.operatingPoint.Q_m3s;
  if (actualQ < targetQ) {
    const deficit = ((1 - actualQ / targetQ) * 100).toFixed(1);
    results.push({
      severity: "hard",
      code: "FLOW_BELOW_TARGET",
      message: `Operating flow rate (${simResult.operatingPoint.Q_GPM.toFixed(1)} GPM) is ${deficit}% below the target (${scenario.targetFlowRate_GPM} GPM). Use a larger pump or reduce system losses.`
    });
  }
}

function checkSimulationSoft(simResult, results) {
  if (!simResult?.success) return;

  const eta = simResult.power.efficiency;
  if (eta < MIN_EFFICIENCY) {
    results.push({
      severity: "soft",
      code: "LOW_EFFICIENCY",
      message: `Pump efficiency at the operating point is ${(eta * 100).toFixed(1)}%, which is below ${(MIN_EFFICIENCY * 100).toFixed(0)}%. The pump may be poorly matched to this system.`
    });
  }

  const v_fps =
    simResult.operatingPoint.velocity_fps ??
    simResult.operatingPoint.velocity_ms * M_TO_FT;
  if (v_fps > MAX_VELOCITY_FPS) {
    results.push({
      severity: "soft",
      code: "HIGH_VELOCITY",
      message: `Fluid velocity (${v_fps.toFixed(1)} ft/s) exceeds ${MAX_VELOCITY_FPS} ft/s. This may cause noise, erosion, or excessive pressure drop.`
    });
  } else if (v_fps < OVERSIZE_VELOCITY_FPS) {
    results.push({
      severity: "soft",
      code: "PIPE_OVERSIZED",
      message: `Fluid velocity (${v_fps.toFixed(1)} ft/s) is very low, suggesting the pipe is oversized. This wastes material and budget without hydraulic benefit.`
    });
  }

  const { majorHeadLoss_m, minorHeadLoss_m } = simResult.losses;
  const totalFriction = majorHeadLoss_m + minorHeadLoss_m;
  if (
    totalFriction > 0 &&
    minorHeadLoss_m / totalFriction > MAX_MINOR_LOSS_RATIO
  ) {
    const pct = ((minorHeadLoss_m / totalFriction) * 100).toFixed(0);
    results.push({
      severity: "soft",
      code: "EXCESSIVE_MINOR_LOSSES",
      message: `Minor losses account for ${pct}% of total friction losses. Consider reducing the number of fittings or using lower-K components.`
    });
  }
}

function checkBudget(totalCost, scenario, results) {
  if (totalCost > scenario.budget) {
    const overage = totalCost - scenario.budget;
    results.push({
      severity: "soft",
      code: "OVER_BUDGET",
      message: `Design is $${overage.toLocaleString()} over the $${scenario.budget.toLocaleString()} budget. Consider less expensive materials or fewer components.`
    });
  }
}

// ─── Main validator ──────────────────────────────────────────────────

/**
 * Validate a student's pumping system design.
 *
 * @param {object} params
 * @param {object[]} params.components   – array of placed component objects (from Vuex placedComponents getter)
 * @param {object}   params.scenario     – scenario object from scenarios.json
 * @param {object|null} params.simResult – output of evaluateDesign(), or null if simulation couldn't run
 * @param {number}   params.totalCost    – running cost total from Vuex
 * @returns {{ passed: boolean, results: Array<{severity: string, code: string, message: string}> }}
 */
export function validateDesign({ components, scenario, simResult, totalCost }) {
  const results = [];
  const fluid = getFluid(scenario.fluidKey);

  checkRequiredComponents(components, results);
  checkMaterialCompatibility(components, scenario, fluid, results);
  checkSimulationHard(simResult, scenario, results);
  checkBudget(totalCost, scenario, results);
  checkSimulationSoft(simResult, results);

  const hasHardFailure = results.some((r) => r.severity === "hard");

  if (!hasHardFailure && simResult?.success) {
    results.unshift({
      severity: "success",
      code: "DESIGN_PASSES",
      message: `Design meets all requirements. Operating at ${simResult.operatingPoint.Q_GPM.toFixed(1)} GPM with ${(simResult.power.efficiency * 100).toFixed(1)}% pump efficiency.`
    });
  }

  return {
    passed: !hasHardFailure,
    results
  };
}
